import type { LogisticsUnitKind, LogisticsUnitTemplatePayload } from './logisticsCatalogService';

/**
 * Referencias típicas de industria (no medidas absolutas de cada unidad física).
 * Contenedores: ISO 668 (serie 1) + interiores habituales de dry van.
 * Pallets: EPAL / ISO / GMA.
 * Camiones: tautliner / mega EU de referencia.
 */
export interface LogisticsUnitPreset {
  id: string;
  kind: LogisticsUnitKind;
  /** Clave i18n bajo logisticsCatalog.presets.* */
  labelKey: string;
  /** Norma / origen corto para tooltip */
  standardHintKey: string;
  values: Pick<
    LogisticsUnitTemplatePayload,
    | 'code'
    | 'name'
    | 'kind'
    | 'internal_length_mm'
    | 'internal_width_mm'
    | 'internal_height_mm'
    | 'max_payload_kg'
    | 'volume_utilization_factor'
    | 'weight_utilization_factor'
    | 'capacity_binding_rule'
  >;
}

export const LOGISTICS_UNIT_PRESETS: LogisticsUnitPreset[] = [
  {
    id: 'cont-20dv',
    kind: 'CONTAINER',
    labelKey: 'presets.items.cont20dv',
    standardHintKey: 'presets.hints.iso668',
    values: {
      code: '20DV',
      name: '20′ Dry Van',
      kind: 'CONTAINER',
      internal_length_mm: 5898,
      internal_width_mm: 2352,
      internal_height_mm: 2393,
      max_payload_kg: 28200,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'cont-40dv',
    kind: 'CONTAINER',
    labelKey: 'presets.items.cont40dv',
    standardHintKey: 'presets.hints.iso668',
    values: {
      code: '40DV',
      name: '40′ Dry Van',
      kind: 'CONTAINER',
      internal_length_mm: 12032,
      internal_width_mm: 2352,
      internal_height_mm: 2393,
      max_payload_kg: 28800,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'cont-40hc',
    kind: 'CONTAINER',
    labelKey: 'presets.items.cont40hc',
    standardHintKey: 'presets.hints.iso668',
    values: {
      code: '40HC',
      name: '40′ High Cube',
      kind: 'CONTAINER',
      internal_length_mm: 12032,
      internal_width_mm: 2352,
      internal_height_mm: 2698,
      max_payload_kg: 28600,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'cont-45hc',
    kind: 'CONTAINER',
    labelKey: 'presets.items.cont45hc',
    standardHintKey: 'presets.hints.iso668',
    values: {
      code: '45HC',
      name: '45′ High Cube',
      kind: 'CONTAINER',
      internal_length_mm: 13556,
      internal_width_mm: 2352,
      internal_height_mm: 2698,
      max_payload_kg: 27600,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'pal-eur1',
    kind: 'PALLET',
    labelKey: 'presets.items.palEur1',
    standardHintKey: 'presets.hints.epal',
    values: {
      code: 'EUR1',
      name: 'EUR / EPAL 1',
      kind: 'PALLET',
      internal_length_mm: 1200,
      internal_width_mm: 800,
      internal_height_mm: 144,
      max_payload_kg: 1500,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'pal-eur2',
    kind: 'PALLET',
    labelKey: 'presets.items.palEur2',
    standardHintKey: 'presets.hints.epal',
    values: {
      code: 'EUR2',
      name: 'EUR / EPAL 2',
      kind: 'PALLET',
      internal_length_mm: 1200,
      internal_width_mm: 1000,
      internal_height_mm: 144,
      max_payload_kg: 1500,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'pal-iso1',
    kind: 'PALLET',
    labelKey: 'presets.items.palIso1',
    standardHintKey: 'presets.hints.iso6780',
    values: {
      code: 'ISO1',
      name: 'ISO 1 (1200×1000)',
      kind: 'PALLET',
      internal_length_mm: 1200,
      internal_width_mm: 1000,
      internal_height_mm: 144,
      max_payload_kg: 1500,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'pal-gma',
    kind: 'PALLET',
    labelKey: 'presets.items.palGma',
    standardHintKey: 'presets.hints.gma',
    values: {
      code: 'GMA48',
      name: 'GMA 48×40″',
      kind: 'PALLET',
      internal_length_mm: 1219,
      internal_width_mm: 1016,
      internal_height_mm: 144,
      max_payload_kg: 2000,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'truck-tautliner',
    kind: 'TRUCK',
    labelKey: 'presets.items.truckTautliner',
    standardHintKey: 'presets.hints.euTrailer',
    values: {
      code: 'TRL13.6',
      name: 'Semirremolque tautliner 13,6 m',
      kind: 'TRUCK',
      internal_length_mm: 13600,
      internal_width_mm: 2450,
      internal_height_mm: 2700,
      max_payload_kg: 25000,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
  {
    id: 'truck-mega',
    kind: 'TRUCK',
    labelKey: 'presets.items.truckMega',
    standardHintKey: 'presets.hints.euTrailer',
    values: {
      code: 'MEGA13.6',
      name: 'Semirremolque mega 13,6 m',
      kind: 'TRUCK',
      internal_length_mm: 13600,
      internal_width_mm: 2480,
      internal_height_mm: 3000,
      max_payload_kg: 24000,
      volume_utilization_factor: 1,
      weight_utilization_factor: 1,
      capacity_binding_rule: 'BOTH',
    },
  },
];

export function presetsForKind(kind: LogisticsUnitKind | '' | undefined): LogisticsUnitPreset[] {
  if (!kind) return LOGISTICS_UNIT_PRESETS;
  return LOGISTICS_UNIT_PRESETS.filter((p) => p.kind === kind);
}
